/**
 * Utility functions for converting Markdown syntax to Phabricator Remarkup.
 */

import { marked } from 'marked';

/**
 * Convert a Markdown string to Phabricator Remarkup string using marked.js tokenizer.
 *
 * This handles common Markdown constructs:
 * - Headings (# to = heading =)
 * - Italics (*text* / _text_ to //text//)
 * - Strikethrough (~~text~~ to --text--)
 * - Code blocks (```code``` to triple-brace {{{ code }}})
 *
 * Lists and inline code are also converted.
 *
 * @param markdown - Input Markdown string
 * @returns Converted Remarkup string
 */
export function convertMarkdownToPhabricatorRemarkup(markdown: string): string {
  const tokens = marked.lexer(markdown);
  let output = '';

  for (const token of tokens) {
    switch (token.type) {
      case 'heading': {
        const prefix = '='.repeat(token.depth);
        output += `${prefix} ${token.text} ${prefix}\n\n`;
        break;
      }
      case 'paragraph': {
        if (token.tokens?.length === 1 && token.tokens[0].type === 'image') {
          const imageToken = token.tokens[0];
          output += `{img ${imageToken.href}}\n\n`;
        } else {
          output += `${token.text}\n\n`;
        }
        break;
      }
      case 'text': {
        output += token.text + '\n';
        break;
      }
      case 'code': {
        output += `{{{${token.text}}}}\n\n`;
        break;
      }
      case 'blockquote': {
        const lines = token.text
          .split('\n')
          .map((line: string) => (line ? '> ' + line : '>'))
          .join('\n');
        output += lines + '\n\n';
        break;
      }
      case 'list': {
        for (const item of token.items) {
          const prefix = token.ordered ? '# ' : '- ';
          output += `  ${prefix}${item.text}\n`;
        }
        output += '\n';
        break;
      }
      case 'hr': {
        output += '---\n\n';
        break;
      }
      case 'space': {
        output += '\n';
        break;
      }
      default: {
        if ('raw' in token && token.raw) {
          output += token.raw + '\n';
        }
      }
    }
  }

  // Apply inline formatting
  output = output
    // Bold+Italic ***text*** -> //**text**//
    .replace(
      /(^|[^*])\*\*\*([\s\S]+?)\*\*\*(?!\*)/g,
      (_match, prefix, content) => `${prefix}//**${content}**//`,
    )
    // Strikethrough ~~text~~ -> --text--
    .replace(/~~([^~]+)~~/g, '--$1--')
    // Italic *text* or _text_ -> //text//
    .replace(
      /(^|[^*])\*([^*]+)\*(?!\*)/g,
      (_match, prefix, content) => `${prefix}//${content}//`,
    )
    .replace(
      /(^|[^_])_([^_]+)_(?!_)/g,
      (_match, prefix, content) => `${prefix}//${content}//`,
    )
    // Inline code `text` -> `text`
    .replace(/`([^`]+)`/g, '`$1`');

  return output.trim();
}


export const convertToBase64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject('Failed to convert file.');
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
}

export const removeBase64Prefix = (base64String: string): string => {
  return base64String.replace(/^data:image\/\w+;base64,/, '');
}